export const QUEEN_CHARLOTTE_LODGE_ID = 34

/**
 * Non-lodge depot locations.
 * @param {Array<{ id?: unknown, is_lodge?: boolean }> | null | undefined} locations
 * @returns {Array<{ id?: unknown, is_lodge?: boolean }>}
 */
export function getDepots(locations) {
    return [...(locations ?? [])].filter((l) => !l.is_lodge)
}

/**
 * Order origin location for pickup rules: `lodge_id` when set, otherwise `location_id`.
 * @param {{ lodge_id?: unknown, location_id?: unknown } | null | undefined} order
 */
export function getOrderOriginLocationId(order) {
    if (order?.lodge_id != null) {
        return order.lodge_id
    }
    return order?.location_id ?? null
}

/**
 * Whether a location may be selected as a pickup location for an order at `originLocationId`.
 * @param {{ id?: unknown, allow_pick_up?: boolean } | null | undefined} location
 * @param {unknown} originLocationId
 */
export function isPickupLocation(location, originLocationId) {
    if (!location) {
        return false
    }
    if (location.allow_pick_up) {
        return true
    }
    return (
        Number(location.id) === Number(originLocationId) &&
        Number(originLocationId) === QUEEN_CHARLOTTE_LODGE_ID
    )
}

/**
 * Pickup locations for orders at `originLocationId` (use `getOrderOriginLocationId` when an order is available).
 * @param {unknown} originLocationId
 * @param {Array<{ id?: unknown, allow_pick_up?: boolean }> | null | undefined} locations
 */
export function getPickupLocations(originLocationId, locations) {
    return [...(locations ?? [])].filter((l) => isPickupLocation(l, originLocationId))
}

/**
 * Whether a location has ever been a pickup location (reporting / filters only).
 * @param {{ id?: unknown, allow_pick_up?: boolean } | null | undefined} location
 */
export function isExhaustivePickupLocation(location) {
    if (!location) {
        return false
    }
    return Boolean(location.allow_pick_up) || Number(location.id) === QUEEN_CHARLOTTE_LODGE_ID
}

/**
 * All locations that may appear as pickup locations on any order (reporting / filters only).
 * @param {Array<{ id?: unknown, allow_pick_up?: boolean }> | null | undefined} locations
 */
export function getExhaustivePickupLocations(locations) {
    return [...(locations ?? [])].filter(isExhaustivePickupLocation)
}

/**
 * Location filter dropdown order: non-lodges by id, then lodges by name.
 * @param {Array<{ id?: number | string, name?: string, is_lodge?: boolean }> | null | undefined} locations
 * @returns {Array<{ id?: number | string, name?: string, is_lodge?: boolean }>}
 */
export function sortLocationsForFilterDropdown(locations) {
    const list = [...(locations ?? [])]
    const nonLodges = list
        .filter((location) => !location.is_lodge)
        .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
    const lodges = list
        .filter((location) => location.is_lodge)
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" }))
    return [...nonLodges, ...lodges]
}

/** @typedef {{ display: string, value: string, disabled?: boolean }} LocationFilterOption */

const LOCATION_FILTER_SEPARATOR = {
    display: "────────────",
    value: "__location_separator__",
    disabled: true,
}

/**
 * Options for the location filter chip: depots, separator, lodges.
 * @param {Array<{ id?: number | string, name?: string, is_lodge?: boolean }> | null | undefined} locations
 * @returns {LocationFilterOption[]}
 */
export function buildLocationFilterOptions(locations) {
    const sorted = sortLocationsForFilterDropdown(locations)
    const nonLodges = sorted.filter((location) => !location.is_lodge)
    const lodges = sorted.filter((location) => location.is_lodge)

    /** @type {LocationFilterOption[]} */
    const options = nonLodges.map((location) => ({
        display: location.name ?? "",
        value: String(location.id),
    }))

    if (nonLodges.length > 0 && lodges.length > 0) {
        options.push({ ...LOCATION_FILTER_SEPARATOR })
    }

    for (const location of lodges) {
        options.push({
            display: location.name ?? "",
            value: String(location.id),
        })
    }

    return options
}

/**
 * Compact location label for narrow (`xs`) order lists: `lodge_code` for lodges, `short_code` otherwise.
 * @param {{ is_lodge?: boolean, lodge_code?: string, short_code?: string, name?: string } | null | undefined} location
 */
export function getLocationShortLabel(location) {
    if (!location) {
        return ""
    }
    if (location.is_lodge) {
        return location.lodge_code ?? ""
    }
    return location.short_code ?? ""
}

